import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { LayoutTemplate, Settings, Trash2, Video as VideoIcon, Play, Link as LinkIcon , Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps, ContentBlock } from '@/types/flow';
import BlocksPreview from './BlocksPreview';

const VideoTemplateNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const buttons = config.buttons || [];
    const hasVideo = !!config.media_url;
    const allButtonsAreUrl = buttons.length > 0 && buttons.every((btn: any) => btn.button_type === 'url');

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-pink-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-pink-50 rounded-lg flex items-center justify-center">
                        <LayoutTemplate className="w-3.5 h-3.5 text-pink-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Template de Vídeo'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Vídeo + Botões</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5 space-y-2">
                {hasVideo ? (
                    <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-100">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow">
                                <Play className="w-5 h-5 text-gray-900 ml-0.5" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="aspect-video bg-gray-50 rounded-lg flex items-center justify-center border border-dashed border-gray-200">
                        <VideoIcon className="w-6 h-6 text-gray-300" />
                    </div>
                )}

                {buttons.length > 0 && (
                    <div className="space-y-1.5 pt-1.5 border-t border-gray-100">
                        {buttons.slice(0, 3).map((button: any, idx: number) => (
                            <div
                                key={button.id || idx}
                                className="relative text-[11px] bg-pink-50 border border-pink-100 rounded-md px-3 py-2 text-pink-700 text-center font-medium flex items-center justify-center gap-1.5 min-h-[34px] overflow-visible"
                            >
                                {button.button_type === 'url' && <LinkIcon className="w-3 h-3 flex-shrink-0" />}
                                <span className="truncate flex-1">{button.button_text || `Botão ${idx + 1}`}</span>

                                {button.button_type === 'postback' && (
                                    <Handle
                                        type="source"
                                        position={Position.Right}
                                        id={`button-${button.id}`}
                                        style={{
                                            position: 'absolute',
                                            right: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '14px',
                                            height: '14px',
                                            background: '#2196F3',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            cursor: 'crosshair',
                                            zIndex: 10,
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                        {buttons.length > 3 && (
                            <span className="text-[10px] text-gray-400 block text-center">+{buttons.length - 3} botões</span>
                        )}
                    </div>
                )}
            </div>

            <BlocksPreview blocks={(config.blocks || []) as ContentBlock[]} />

            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-pink-600 hover:bg-pink-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" position={Position.Left} id="target" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />

            {(allButtonsAreUrl || buttons.length === 0) && (
                <Handle type="source" position={Position.Right} id="source" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            )}
        </div>
    );
};

export default memo(VideoTemplateNode);
